import { useState, useCallback, useMemo, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router";
import { Navigation, NavSection } from "@repo/ui";
import { useConfigurationSync } from "~/hooks/use-sync";
import { SidebarDialogs } from "./dialogs/sidebar-dialogs";
import { TasksSection } from "./sections/tasks-sections";
import { SidebarUserFooter } from "./sections/user-footer";
import { administrationNavItems, navigationItems } from "./side-bar.data";
import { SidebarProvider } from "./sidebar-context";
import type { RouteContextType } from "./sidebar-context";
import { NavItemRenderer } from "../side-bar/nav-item-renderer";

export type { RouteContextType } from "./sidebar-context";

export const SidebarNavigation = () => {
  const [showAdministration, setShowAdministration] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useConfigurationSync("Brand");

  const isAdmin = useMemo(() => {
    const roles = user?.profile?.role || user?.profile?.roles || [];
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes("Administrator");
  }, [user]);

  useEffect(() => {
    if (location.pathname.startsWith("/administration") && isAdmin) {
      setShowAdministration(true);
    } else {
      setShowAdministration(false);
    }
  }, [location.pathname, isAdmin]);

  const handlers = useMemo(
    () => ({
      trackBrandSettings: () => console.log("Brand Settings clicked"),
      trackListsView: () => console.log("Lists viewed"),
      trackPermissionsAccess: () => console.log("Permissions accessed"),
      toggleAdministration: () => setShowAdministration(true),
      toggleDashboard: () => setShowAdministration(false),
      onTaskClick: (entityId: string) => {
        const taskIdStr = entityId.replace("task-", "");
        const searchParams = new URLSearchParams(window.location.search);
        const currentOpenTasks = searchParams.get("openTasks");

        const currentIds = currentOpenTasks ? currentOpenTasks.split(",") : [];
        if (currentIds.includes(taskIdStr)) {
          return;
        }
        const newIds = new Set(currentIds);
        newIds.add(taskIdStr);

        const nextOpenTasksParam = Array.from(newIds).join(",");
        searchParams.set("openTasks", nextOpenTasksParam);

        navigate(`/dashboard/tasks?${searchParams.toString()}`);
      },
    }),
    [navigate]
  );

  const isActive = useCallback((path: string) => location.pathname === path, [location.pathname]);

  const isActivePrefix = useCallback(
    (prefix: string, level: number = 1) => {
      const segments = location.pathname.split("/").filter(Boolean);
      const pathAtLevel = "/" + segments.slice(0, level).join("/");
      return prefix.startsWith(pathAtLevel);
    },
    [location.pathname]
  );

  const isChildRoute = useCallback(
    (path: string) => location.pathname.startsWith(path),
    [location.pathname]
  );

  const routeContext: RouteContextType = useMemo(
    () => ({ isActive, isActivePrefix, isChildRoute }),
    [isActive, isActivePrefix, isChildRoute]
  );

  return (
    <SidebarProvider routeContext={routeContext} handlers={handlers}>
      <SidebarDialogs />
      <Navigation footer={<SidebarUserFooter />}>
        {isAdmin && (
          <>
            <NavSection title="Navigation" defaultOpen={true} showChevron={isAdmin ? true : false}>
              <NavItemRenderer showTreeLines={false} items={navigationItems} />
            </NavSection>
            {isActivePrefix("/administration") && (
              <NavSection
                title="Administration"
                defaultOpen={showAdministration}
                toggleState={showAdministration}
                showBorderLine={false}
              >
                <NavItemRenderer showTreeLines={false} items={administrationNavItems} />
              </NavSection>
            )}
          </>
        )}
        {!showAdministration && <TasksSection />}
      </Navigation>
    </SidebarProvider>
  );
};

SidebarNavigation.displayName = "SidebarNavigation";
